import type { CustomPropertyDto, EntityDto, EntityImageDto, ReferenceLinkDto, TreeRelationDto } from "api/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { flipRelationPerspective } from "utilities";

import type { AttributeRow, EntityDetailsPanelProps, RelationshipRow } from "./entityDetailsPanel.types";
import EntityImageManager from "./EntityImageManager";
import type { GalleryImage } from "./entityImages.types";
import ImageLightbox, { type LightboxImage } from "./ImageLightbox";
import LineIcon from "./LineIcon";
import RelationshipItem from "./RelationshipItem";

import styles from "./EntityDetailsPanel.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "?";

const EntityDetailsPanel = ({
	entities,
	entity,
	isDeleting = false,
	onClose,
	onDelete,
	onEdit,
	onSelectEntity,
	relations
}: EntityDetailsPanelProps) => {
	const { t } = useTranslation("homePage");

	// The panel only *displays* photos — adding/removing/setting-default happens in the Edit dialog.
	// Driven straight off the entity prop so it refreshes after an edit saves.
	const galleryImages = useMemo<GalleryImage[]>(
		() =>
			[...(entity.images ?? [])]
				.sort((a: EntityImageDto, b: EntityImageDto) => a.sortOrder - b.sortOrder)
				.map((image: EntityImageDto) => ({
					id: image.id,
					url: image.url,
					name: image.name,
					isDefault: image.isDefault
				})),
		[entity.images]
	);

	// The lightbox shows the gallery when present, otherwise the legacy single image (pre-gallery nodes).
	const lightboxImages = useMemo<LightboxImage[]>(() => {
		if (galleryImages.length > 0) {
			return galleryImages.map((image: GalleryImage) => ({ url: image.url, name: image.name }));
		}

		return entity.imageUrl ? [{ url: entity.imageUrl, name: entity.imageName }] : [];
	}, [entity.imageName, entity.imageUrl, galleryImages]);

	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
	useEffect(() => {
		setLightboxIndex(null);
	}, [entity.id]);

	// Index of the default image (or the first) — where clicking the hero opens the lightbox.
	const heroIndex = useMemo(() => {
		const defaultIndex = galleryImages.findIndex((image: GalleryImage) => image.isDefault);

		return defaultIndex >= 0 ? defaultIndex : 0;
	}, [galleryImages]);
	const heroImage = lightboxImages[heroIndex] ?? lightboxImages[0] ?? null;

	// A broken/expired image URL falls back to initials instead of an invisible broken <img>.
	const [imageFailed, setImageFailed] = useState(false);
	useEffect(() => {
		setImageFailed(false);
	}, [entity.id]);
	const onImageError = useCallback((): void => setImageFailed(true), []);

	const openHeroLightbox = useCallback(
		(): void => setLightboxIndex(heroImage ? heroIndex : 0),
		[heroImage, heroIndex]
	);
	const closeLightbox = useCallback((): void => setLightboxIndex(null), []);
	const noop = useCallback((): void => undefined, []);

	// Only show the thumbnail strip when there's more than one photo to browse; a single photo is
	// reachable by clicking the hero. Management controls never appear here (view-only).
	const showPhotosSection = galleryImages.length > 1;

	const attributes = useMemo<AttributeRow[]>(() => {
		const gender = entity.gender === "Other" ? entity.genderOther?.trim() || entity.gender : entity.gender;
		const rows: AttributeRow[] = [
			{ label: t("editor.entityPanel.gender"), value: gender ?? "" },
			{ label: t("editor.entityPanel.born"), value: entity.born ?? "" },
			{ label: t("editor.entityPanel.title"), value: entity.personTitle ?? "" },
			{ label: t("editor.entityPanel.alias"), value: entity.alias ?? "" },
			{ label: t("editor.entityPanel.nickname"), value: entity.nickname ?? "" },
			{ label: t("editor.entityPanel.specialty"), value: entity.specialty ?? "" },
			{ label: t("editor.entityPanel.country"), value: entity.country ?? "" },
			{ label: t("editor.entityPanel.world"), value: entity.world ?? "" },
			{ label: t("editor.entityPanel.placeType"), value: entity.placeType ?? "" },
			{ label: t("editor.entityPanel.region"), value: entity.region ?? "" },
			{ label: t("editor.entityPanel.latitude"), value: entity.latitude != null ? String(entity.latitude) : "" },
			{
				label: t("editor.entityPanel.longitude"),
				value: entity.longitude != null ? String(entity.longitude) : ""
			},
			{ label: t("editor.entityPanel.objectType"), value: entity.objectType ?? "" },
			{ label: t("editor.entityPanel.origin"), value: entity.origin ?? "" },
			{ label: t("editor.entityPanel.material"), value: entity.material ?? "" },
			{ label: t("editor.entityPanel.currentLocation"), value: entity.currentLocation ?? "" },
			{ label: t("editor.entityPanel.era"), value: entity.era ?? "" },
			{ label: t("editor.entityPanel.creatureType"), value: entity.creatureType ?? "" },
			{ label: t("editor.entityPanel.species"), value: entity.speciesName ?? "" },
			{ label: t("editor.entityPanel.alignment"), value: entity.alignment ?? "" },
			{ label: t("editor.entityPanel.traits"), value: entity.traits ?? "" },
			{ label: t("editor.entityPanel.organizationType"), value: entity.organizationType ?? "" },
			{ label: t("editor.entityPanel.motto"), value: entity.motto ?? "" },
			{ label: t("editor.entityPanel.founded"), value: entity.founded ?? "" },
			{ label: t("editor.entityPanel.eventType"), value: entity.eventType ?? "" },
			{ label: t("editor.entityPanel.date"), value: entity.occurredOn ?? "" },
			{ label: t("editor.entityPanel.participants"), value: entity.participants ?? "" },
			{ label: t("editor.entityPanel.outcome"), value: entity.outcome ?? "" },
			{ label: t("editor.entityPanel.classification"), value: entity.classification ?? "" },
			{ label: t("editor.entityPanel.habitat"), value: entity.habitat ?? "" },
			{ label: t("editor.entityPanel.lifespan"), value: entity.lifespan ?? "" },
			{ label: t("editor.entityPanel.language"), value: entity.language ?? "" },
			{ label: t("editor.entityPanel.category"), value: entity.conceptType ?? "" },
			{ label: t("editor.entityPanel.domain"), value: entity.domain ?? "" },
			{ label: t("editor.entityPanel.source"), value: entity.source ?? "" },
			{ label: t("editor.entityPanel.symbols"), value: entity.symbols ?? "" },
			{ label: t("editor.entityPanel.scope"), value: entity.scope ?? "" },
			{ label: t("editor.entityPanel.status"), value: entity.status ?? "" },
			{ label: t("editor.entityPanel.schemaLabel"), value: entity.customTypeLabel ?? "" },
			{ label: t("editor.entityPanel.parentGroup"), value: entity.parentGroup ?? "" },
			{ label: t("editor.entityPanel.tags"), value: entity.tags ?? "" },
			...(entity.customProperties ?? []).map((prop: CustomPropertyDto) => ({
				label: prop.key,
				value: prop.value ?? ""
			}))
		];

		return rows.filter((row: AttributeRow) => row.value.trim().length > 0);
	}, [entity, t]);

	// Tree relations are mirrored pairs; rows pointing at this entity read "<from> is <type> of this
	// entity". They are listed under this entity's own heading, though, so each one is flipped to read
	// from its perspective — the same flip the form dialogs apply. Printing the stored type raw made
	// the list assert the opposite of the data for every directional pair: Kuruk's panel showed
	// "Predecessor Of -> Yangchen" for a row that says Yangchen preceded Kuruk.
	const relationships = useMemo<RelationshipRow[]>(
		() =>
			relations
				.filter((relation: TreeRelationDto) => relation.toEntityId === entity.id)
				.flatMap((relation: TreeRelationDto) => {
					const related = entities.find((item: EntityDto) => item.id === relation.fromEntityId);

					return related
						? [
								{
									id: relation.id,
									relatedEntity: related,
									relationType: flipRelationPerspective(relation.relationType),
									isEx: relation.isEx
								}
						  ]
						: [];
				}),
		[entities, entity.id, relations]
	);

	return (
		<aside aria-label={t("editor.entityPanel.selectedEntity")} className={styles.entityDetailsPanel}>
			<header className={styles.entityDetailsHeader}>
				<span className={styles.entityDetailsKicker}>{t("editor.entityPanel.selectedEntity")}</span>
				<button
					aria-label={t("editor.entityPanel.close")}
					className={styles.entityDetailsClose}
					onClick={onClose}
					type="button">
					<LineIcon name="close" />
				</button>
			</header>

			<div className={styles.entityDetailsBody}>
				<div className={styles.entityDetailsHero}>
					{heroImage && !imageFailed ? (
						<button
							aria-label={t("editor.imageGallery.view")}
							className={styles.entityDetailsHeroButton}
							onClick={openHeroLightbox}
							type="button">
							<img
								alt={entity.title}
								className={styles.entityDetailsImage}
								onError={onImageError}
								src={heroImage.url}
							/>
						</button>
					) : (
						<span className={styles.entityDetailsInitials}>{getInitials(entity.title)}</span>
					)}
				</div>

				{showPhotosSection && (
					<section>
						<h3 className={styles.entityDetailsSectionTitle}>{t("editor.entityPanel.photos")}</h3>
						<EntityImageManager
							busy={false}
							editable={false}
							failed={false}
							images={galleryImages}
							onAddFiles={noop}
							onOpen={setLightboxIndex}
							onRemove={noop}
							onSetDefault={noop}
						/>
					</section>
				)}

				<div>
					<h2 className={styles.entityDetailsName}>{entity.title}</h2>
					{entity.personTitle && <p className={styles.entityDetailsRole}>{entity.personTitle}</p>}
				</div>

				{attributes.length > 0 && (
					<section>
						<h3 className={styles.entityDetailsSectionTitle}>{t("editor.entityPanel.attributes")}</h3>
						<dl className={styles.entityDetailsAttrs}>
							{attributes.map((row: AttributeRow) => (
								<React.Fragment key={row.label}>
									<dt>{row.label}:</dt>
									<dd>{row.value}</dd>
								</React.Fragment>
							))}
						</dl>
					</section>
				)}

				{entity.description?.trim() && <p className={styles.entityDetailsDescription}>{entity.description}</p>}

				<section>
					<h3 className={styles.entityDetailsSectionTitle}>{t("editor.entityPanel.relationships")}</h3>
					{relationships.length === 0 ? (
						<p className={styles.entityDetailsEmpty}>{t("editor.entityPanel.noRelationships")}</p>
					) : (
						<ul className={styles.entityDetailsRelations}>
							{relationships.map((row: RelationshipRow) => (
								<RelationshipItem key={row.id} onSelect={onSelectEntity} row={row} />
							))}
						</ul>
					)}
				</section>

				{(entity.referenceLinks?.length ?? 0) > 0 && (
					<section>
						<h3 className={styles.entityDetailsSectionTitle}>{t("editor.entityPanel.references")}</h3>
						<ul className={styles.entityDetailsReferences}>
							{entity.referenceLinks!.map((link: ReferenceLinkDto) => (
								<li key={link.id}>
									<a href={link.url} rel="noopener noreferrer" target="_blank">
										{link.url}
									</a>
								</li>
							))}
						</ul>
					</section>
				)}
			</div>

			{(onEdit || onDelete) && (
				<div className={styles.entityDetailsActions}>
					{onEdit && (
						<button
							className={styles.entityDetailsEditButton}
							disabled={isDeleting}
							onClick={onEdit}
							type="button">
							<LineIcon name="pencil" />
							<span>{t("editor.entityPanel.edit")}</span>
						</button>
					)}
					{onDelete && (
						<button
							className={styles.entityDetailsDeleteButton}
							disabled={isDeleting}
							onClick={onDelete}
							type="button">
							<LineIcon name="trash" />
							<span>
								{isDeleting ? t("editor.entityPanel.deleting") : t("editor.entityPanel.delete")}
							</span>
						</button>
					)}
				</div>
			)}

			{lightboxIndex !== null && lightboxImages.length > 0 && (
				<ImageLightbox
					images={lightboxImages}
					index={Math.min(lightboxIndex, lightboxImages.length - 1)}
					onClose={closeLightbox}
					onIndexChange={setLightboxIndex}
					title={entity.title}
				/>
			)}
		</aside>
	);
};

export default EntityDetailsPanel;
